import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { fetchChatCanais, fetchChatNaoLidas } from '../services/api';

const ChatNotificationContext = createContext(null);

export const useChatNotification = () => {
  const ctx = useContext(ChatNotificationContext);
  if (!ctx) {
    // Retornar valores padrão se o provider não estiver montado
    return { totalNaoLidas: 0, socketRef: { current: null }, ultimaMensagem: null };
  }
  return ctx;
};

export const ChatNotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [totalNaoLidas, setTotalNaoLidas] = useState(0);
  // Última mensagem recebida — usado pelo ChatFlutuante para gerar toasts
  const [ultimaMensagem, setUltimaMensagem] = useState(null);
  const socketRef = useRef(null);
  const audioCtxRef = useRef(null);

  // ============================================
  // SOM DE NOTIFICAÇÃO (gerado via Web Audio API)
  // ============================================
  const tocarSomNotificacao = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      const now = ctx.currentTime;

      // Nota 1 — tom suave
      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(880, now); // Lá5
      gain1.gain.setValueAtTime(0.15, now);
      gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
      osc1.connect(gain1);
      gain1.connect(ctx.destination);
      osc1.start(now);
      osc1.stop(now + 0.3);

      // Nota 2 — harmonia
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(1174.66, now + 0.1); // Ré6
      gain2.gain.setValueAtTime(0, now);
      gain2.gain.linearRampToValueAtTime(0.12, now + 0.12);
      gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.45);
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.start(now + 0.1);
      osc2.stop(now + 0.45);
    } catch (e) {
      // Navegador pode bloquear áudio antes de interação do usuário
      console.log('🔇 Áudio bloqueado pelo navegador');
    }
  }, []);

  // ============================================
  // BUSCAR TOTAL DE NÃO LIDAS
  // ============================================
  const atualizarNaoLidas = useCallback(async () => {
    const result = await fetchChatNaoLidas();
    if (result.success) {
      setTotalNaoLidas(result.total || 0);
    }
  }, []);

  // ============================================
  // SOCKET.IO GLOBAL — NOTIFICAÇÕES DO CHAT
  // ============================================
  useEffect(() => {
    if (!user?.id) return;

    const socketUrl = window.location.origin;
    const socket = io(`${socketUrl}/chat`, {
      transports: ['websocket', 'polling']
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('🔔 [ChatNotification] Socket global do chat conectado');
      socket.emit('autenticar', { userId: user.id, nome: user.nome });
    });

    // Carregar canais para entrar nos rooms
    const entrarNosCanais = async () => {
      const result = await fetchChatCanais();
      if (result.success && result.data) {
        socket.emit('join-canais', {
          canalIds: result.data.map(c => c.id)
        });
      }
    };

    socket.on('connect', entrarNosCanais);

    // Nova mensagem recebida — tocar som e atualizar estado
    socket.on('nova-mensagem', (mensagem) => {
      if (mensagem.usuario_id !== user.id) {
        tocarSomNotificacao();
        // Expor para que o ChatFlutuante possa gerar o toast
        setUltimaMensagem({ ...mensagem, _ts: Date.now() });
      }
      // Atualizar contador de não lidas
      atualizarNaoLidas();
    });

    // Grupo criado — entrar no room e atualizar lista
    socket.on('grupo-criado', (data) => {
      if (data.membros?.includes(user.id)) {
        socket.emit('join-canal', { canalId: data.canal_id });
        atualizarNaoLidas();
      }
    });

    // Membro adicionado a grupo — entrar no room se for o próprio usuário
    socket.on('membro-adicionado', (data) => {
      if (data.usuario_ids?.includes(user.id)) {
        socket.emit('join-canal', { canalId: data.canal_id });
        atualizarNaoLidas();
      }
    });

    // Membro removido — se for o próprio usuário, sem ação especial (já tratado na UI)
    socket.on('membro-removido', (data) => {
      if (data.usuario_id === user.id) {
        atualizarNaoLidas();
      }
    });

    // Heartbeat a cada 30s para manter presença
    const pingInterval = setInterval(() => {
      socket.emit('ping-presenca');
    }, 30000);

    // Buscar não lidas ao iniciar
    atualizarNaoLidas();

    // Atualizar não lidas periodicamente (fallback)
    const naoLidasInterval = setInterval(atualizarNaoLidas, 60000);

    return () => {
      socket.disconnect();
      socketRef.current = null;
      clearInterval(pingInterval);
      clearInterval(naoLidasInterval);
    };
  }, [user?.id, user?.nome, tocarSomNotificacao, atualizarNaoLidas]);

  const value = {
    totalNaoLidas,
    setTotalNaoLidas,
    atualizarNaoLidas,
    socketRef,
    tocarSomNotificacao,
    ultimaMensagem,   // ← nova prop consumida pelo ChatFlutuante
  };

  return (
    <ChatNotificationContext.Provider value={value}>
      {children}
    </ChatNotificationContext.Provider>
  );
};

export default ChatNotificationContext;
